import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { EvaluationAnswerFormT, LessonQuestionI } from 'types';
import { evaluationSchema } from '../schemas/evaluation-schema';
import useTranslation from 'Hooks/useTranslation';
import useModal from 'Hooks/useModal';

interface UseQuestionnaireProps {
  questions: LessonQuestionI[];
}

export const useQuestionnaire = ({ questions }: UseQuestionnaireProps) => {
  const { t2 } = useTranslation('course_test');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [currentQuestion, setCurrentQuestion] = useState<LessonQuestionI | null>(
    questions && questions.length > 0 ? questions[0] : null
  );

  const { open, openModal, closeModal } = useModal();

  const {
    control,
    register,
    watch,
    formState: { errors, isDirty },
    setValue,
  } = useForm<EvaluationAnswerFormT>({
    resolver: zodResolver(evaluationSchema),
    defaultValues: {
      answers: questions.map(q => ({
        questionId: q.questionId,
        answer: '',
      })),
    },
  });

  const answers = watch('answers');

  useEffect(() => {
    if (questions && questions.length > 0) {
      setCurrentQuestion(questions[0]);
      setValue(
        'answers',
        questions.map(q => ({
          questionId: q.questionId,
          answer: '',
        }))
      );
    } else {
      setCurrentQuestion(null);
    }
  }, [questions, setValue]);

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setCurrentQuestion(questions[nextIndex]);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      setCurrentQuestion(questions[prevIndex]);
    }
  };

  const handleQuestionSelect = (index: number) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
      setCurrentQuestion(questions[index]);
    }
  };

  return {
    t2,
    open,
    errors,
    answers,
    control,
    isDirty,
    register,
    openModal,
    closeModal,
    currentQuestion,
    handleNextQuestion,
    currentQuestionIndex,
    handleQuestionSelect,
    handlePreviousQuestion,
    isFirstQuestion: currentQuestionIndex === 0,
    isLastQuestion: currentQuestionIndex === questions.length - 1,
  };
};
